import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  createEditorialProject,
  fetchEditorialProjects,
  fetchGatewayDirectory,
  moderateEditorialProject,
  submitEditorialProject,
  updateEditorialProject,
} from '../api';
import { stepsForKind, type EditorialWizardField } from '../editorialWizardSteps';
import {
  isEditorialOpenedFromHub,
  U153_EDITORIAL_FROM_HUB,
} from '../hubEntryFlags';
import type { DirectoryUser, EditorialPersonEntry, EditorialProject } from '../types';

type EditorialView = 'CATEGORY' | 'WIZARD' | 'PREVIEW' | 'LIST';
type PreviewTab = 'PUBLIC' | 'FORM' | 'CHECKS' | 'DOCS' | 'ACCESS';

interface EditorialProjectOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
}

function fieldValue(data: Record<string, unknown>, field: EditorialWizardField): string {
  const current = data[field.id];
  if (current != null && current !== '') return String(current);
  if (field.value) return field.value;
  if (typeof field.suggestion === 'string') return field.suggestion;
  return '';
}

function fieldArrayValue(data: Record<string, unknown>, field: EditorialWizardField): string[] {
  const current = data[field.id];
  if (Array.isArray(current)) return current.map(String);
  if (typeof field.suggestion === 'object' && Array.isArray(field.suggestion)) {
    return field.suggestion.map(String);
  }
  return [];
}

function peopleValue(data: Record<string, unknown>, fieldId: string): EditorialPersonEntry[] {
  const current = data[fieldId];
  if (!Array.isArray(current)) return [];
  return current.filter(
    (item): item is EditorialPersonEntry =>
      item != null && typeof item === 'object' && 'id' in item && 'name' in item
  );
}

function deriveTeamFromFormData(data: Record<string, unknown>): Array<{ name: string; role: string; email?: string }> {
  const buckets = [data.team, data.editorialBoard, data.editorInChief];
  const seen = new Set<string>();
  const team: Array<{ name: string; role: string; email?: string }> = [];

  for (const bucket of buckets) {
    const items = Array.isArray(bucket) ? bucket : bucket ? [bucket] : [];
    for (const raw of items) {
      if (!raw || typeof raw !== 'object') continue;
      const entry = raw as EditorialPersonEntry;
      const key = String(entry.id || entry.email || entry.name);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      team.push({
        name: String(entry.name || ''),
        role: String(entry.role || ''),
        email: entry.email ? String(entry.email) : undefined,
      });
    }
  }
  return team;
}

export const EditorialProjectOverlay: React.FC<EditorialProjectOverlayProps> = ({
  isOpen,
  onClose,
  onBack,
}) => {
  const { user } = useAuth();
  const profileOrg = 'ЕНУ им. Л.Н. Гумилева';
  const profileContact = `${user?.fullName || 'Редактор'} · ${user?.email || ''}`;

  const [view, setView] = useState<EditorialView>('CATEGORY');
  const [projects, setProjects] = useState<EditorialProject[]>([]);
  const [currentProject, setCurrentProject] = useState<EditorialProject | null>(null);
  const [wizardStep, setWizardStep] = useState(0);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [previewTab, setPreviewTab] = useState<PreviewTab>('PUBLIC');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [directory, setDirectory] = useState<DirectoryUser[]>([]);
  const [peopleSearch, setPeopleSearch] = useState('');
  const [peoplePickField, setPeoplePickField] = useState<string | null>(null);
  const [selectedDirectoryId, setSelectedDirectoryId] = useState('');

  const kind = currentProject?.kind || 'CONFERENCE';
  const steps = useMemo(() => stepsForKind(kind), [kind]);
  const isPreviewStep = wizardStep === steps.length - 1;

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    fetchEditorialProjects()
      .then((data) => setProjects(Array.isArray(data) ? data : []))
      .catch(() => setProjects([]));
    fetchGatewayDirectory()
      .then((data) => setDirectory(Array.isArray(data) ? data : []))
      .catch(() => setDirectory([]));

    return () => {
      if (!document.querySelector('.rg-overlay')) {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const timer = window.setTimeout(() => {
      fetchGatewayDirectory(peopleSearch.trim() || undefined)
        .then((data) => setDirectory(Array.isArray(data) ? data : []))
        .catch(() => setDirectory([]));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [isOpen, peopleSearch]);

  const seedIdentityFields = useCallback(
    (data: Record<string, unknown>) => ({
      ...data,
      organizer: data.organizer || profileOrg,
      contact: data.contact || profileContact,
      publisher: data.publisher || profileOrg,
      contacts: data.contacts || profileContact,
    }),
    [profileContact, profileOrg]
  );

  const persistProject = useCallback(
    async (
      project: EditorialProject,
      patch: {
        step?: number;
        data?: Record<string, unknown>;
        title?: string;
        description?: string;
        team?: EditorialProject['team'];
        accepted?: Record<string, unknown>;
      }
    ) => {
      const updated = await updateEditorialProject(project.id, patch);
      setCurrentProject(updated);
      setProjects((prev) => {
        const exists = prev.some((p) => p.id === updated.id);
        return exists ? prev.map((p) => (p.id === updated.id ? updated : p)) : [updated, ...prev];
      });
      if (patch.data) setFormData(patch.data);
      return updated;
    },
    []
  );

  const startCategory = async (selectedKind: 'CONFERENCE' | 'JOURNAL') => {
    setBusy(true);
    setMessage(null);
    try {
      const created = await createEditorialProject({
        kind: selectedKind,
        as_draft: true,
        title: '',
        step: 0,
        data: seedIdentityFields({}),
      });
      setCurrentProject(created);
      setFormData(seedIdentityFields(created.data || {}));
      setWizardStep(0);
      setView('WIZARD');
    } catch (err: any) {
      setMessage(err.message || 'Ошибка создания черновика');
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = async (step = wizardStep, data = formData) => {
    if (!currentProject) return;
    setBusy(true);
    setMessage(null);
    try {
      const title =
        String(data.titleRu || data.title || currentProject.title || '').trim() ||
        (kind === 'CONFERENCE' ? 'Черновик конференции' : 'Черновик журнала');
      await persistProject(currentProject, {
        step,
        data,
        title,
        description: String(data.description || data.aimsScope || currentProject.description || ''),
        team: deriveTeamFromFormData(data),
      });
      setMessage('Черновик сохранён');
    } catch (err: any) {
      setMessage(err.message || 'Ошибка сохранения');
    } finally {
      setBusy(false);
    }
  };

  const goStep = async (next: number) => {
    if (!currentProject) return;
    const bounded = Math.max(0, Math.min(steps.length - 1, next));
    await saveDraft(bounded);
    setWizardStep(bounded);
  };

  const submitProject = async () => {
    if (!currentProject) return;
    setBusy(true);
    setMessage(null);
    try {
      await saveDraft(wizardStep);
      const submitted = await submitEditorialProject(currentProject.id);
      setCurrentProject(submitted);
      setProjects((prev) => prev.map((p) => (p.id === submitted.id ? submitted : p)));
      setPreviewTab('PUBLIC');
      setView('PREVIEW');
      setMessage('Проект отправлен на модерацию');
    } catch (err: any) {
      setMessage(err.message || 'Ошибка отправки');
    } finally {
      setBusy(false);
    }
  };

  const continueDraft = (project: EditorialProject) => {
    setCurrentProject(project);
    setFormData(seedIdentityFields(project.data || {}));
    setWizardStep(project.step ?? 0);
    if (project.status === 'DRAFT' || (project.step ?? 0) < stepsForKind(project.kind).length - 1) {
      setView('WIZARD');
    } else {
      setPreviewTab('PUBLIC');
      setView('PREVIEW');
    }
  };

  /**
   * Canonical pair to etalon `window.__U153_EDITORIAL_FROM_HUB__`.
   * Root of R5.14 (CATEGORY) returns to Create Request Hub via onBack;
   * the hub-entry flag proves this overlay was opened from U153.
   */
  const returnToCreateRequestHub = () => {
    const fromHub = isEditorialOpenedFromHub();
    if (currentProject) {
      void saveDraft(wizardStep, formData);
    }
    onBack();
    return fromHub;
  };

  const handleBack = () => {
    setMessage(null);

    if (view === 'WIZARD') {
      if (wizardStep === 0) {
        // Leave wizard → category home; keep draft mount for Hub «Продолжить».
        if (currentProject) void saveDraft(0, formData);
        setView('CATEGORY');
        return;
      }
      void goStep(wizardStep - 1);
      return;
    }

    if (view === 'PREVIEW') {
      if (currentProject?.status === 'DRAFT') {
        setView('WIZARD');
        setWizardStep(Math.max(0, steps.length - 1));
        return;
      }
      setView('CATEGORY');
      return;
    }

    if (view === 'LIST') {
      setView('CATEGORY');
      return;
    }

    // CATEGORY root → Create Request Hub (closes __U153_EDITORIAL_FROM_HUB__ contract).
    returnToCreateRequestHub();
  };

  const handleModerate = async (projectId: string, action: string) => {
    try {
      const updated = await moderateEditorialProject(projectId, action, 'Решение модератора');
      setProjects((prev) => prev.map((p) => (p.id === projectId ? updated : p)));
      if (currentProject?.id === projectId) setCurrentProject(updated);
    } catch (err: any) {
      setMessage(err.message || 'Ошибка модерации');
    }
  };

  const addPersonFromDirectory = (field: EditorialWizardField, user: DirectoryUser) => {
    const entry: EditorialPersonEntry = {
      id: user.id,
      name: user.full_name,
      role: user.role_label || user.position || 'Участник',
      email: user.email || undefined,
      org: user.organization || undefined,
    };
    setFormData((d) => {
      const current = peopleValue(d, field.id);
      if (field.single) {
        return { ...d, [field.id]: [entry] };
      }
      if (current.some((p) => p.id === entry.id)) return d;
      return { ...d, [field.id]: [...current, entry] };
    });
    setSelectedDirectoryId('');
    setPeoplePickField(null);
  };

  const removePerson = (fieldId: string, personId: string) => {
    setFormData((d) => ({
      ...d,
      [fieldId]: peopleValue(d, fieldId).filter((p) => p.id !== personId),
    }));
  };

  const renderPeopleField = (field: EditorialWizardField) => {
    const selected = peopleValue(formData, field.id);
    const selectedIds = new Set(selected.map((p) => p.id));
    const available = directory.filter((u) => !selectedIds.has(u.id));
    const isPickerOpen = peoplePickField === field.id;
    const minHint = field.minPeople ? `Минимум ${field.minPeople}` : null;

    return (
      <div key={field.id} className={field.wide ? 'md:col-span-2' : ''}>
        <label>
          {field.label}
          {field.required ? ' *' : ''}
          {minHint ? ` · ${minHint}` : ''}
        </label>
        <div className="rg-people">
          {selected.length === 0 ? (
            <p className="rg-people-empty">Выберите участников из базы платформы</p>
          ) : (
            selected.map((person) => (
              <div key={person.id} className="rg-people-chip">
                <div className="rg-people-chip-main">
                  <strong>{person.name}</strong>
                  <span>{person.role}</span>
                  {person.org ? <span>{person.org}</span> : null}
                </div>
                <button
                  type="button"
                  className="rg-btn"
                  aria-label={`Удалить ${person.name}`}
                  onClick={() => removePerson(field.id, person.id)}
                >
                  ×
                </button>
              </div>
            ))
          )}
          {isPickerOpen ? (
            <div className="rg-people-picker">
              <input
                type="search"
                placeholder="Поиск по ФИО, email, организации…"
                value={peopleSearch}
                onChange={(e) => setPeopleSearch(e.target.value)}
              />
              <select
                value={selectedDirectoryId}
                onChange={(e) => setSelectedDirectoryId(e.target.value)}
              >
                <option value="">Выберите человека…</option>
                {available.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.full_name} · {user.role_label || user.position} · {user.organization || '—'}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="rg-btn primary"
                disabled={!selectedDirectoryId}
                onClick={() => {
                  const user = directory.find((u) => u.id === selectedDirectoryId);
                  if (user) addPersonFromDirectory(field, user);
                }}
              >
                Добавить из базы
              </button>
              <button
                type="button"
                className="rg-btn"
                onClick={() => {
                  setPeoplePickField(null);
                  setPeopleSearch('');
                  setSelectedDirectoryId('');
                }}
              >
                Закрыть
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="rg-btn"
              onClick={() => {
                setPeoplePickField(field.id);
                setPeopleSearch('');
                setSelectedDirectoryId('');
              }}
            >
              {field.single && selected.length >= 1 ? 'Заменить из базы' : 'Добавить из базы'}
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderField = (field: EditorialWizardField) => {
    const locked = field.locked;
    const wideClass = field.wide ? 'md:col-span-2' : '';

    if (field.type === 'people') {
      return renderPeopleField(field);
    }

    if (field.type === 'checkboxes') {
      const selected = fieldArrayValue(formData, field);
      return (
        <div key={field.id} className={wideClass}>
          <label>{field.label}{field.required ? ' *' : ''}</label>
          <div className="rg-option-grid">
            {(field.options || []).map((option, index) => (
              <label
                key={option}
                className={`rg-option${selected.includes(option) ? ' is-selected' : ''}`}
              >
                <span className="rg-option-num">{String(index + 1).padStart(2, '0')}</span>
                <span className="rg-option-copy">{option}</span>
                <input
                  type="checkbox"
                  checked={selected.includes(option)}
                  disabled={locked}
                  onChange={() => {
                    const next = selected.includes(option)
                      ? selected.filter((v) => v !== option)
                      : [...selected, option];
                    setFormData((d) => ({ ...d, [field.id]: next }));
                  }}
                />
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (field.type === 'textarea') {
      return (
        <div key={field.id} className={wideClass}>
          <label>{field.label}{field.required ? ' *' : ''}</label>
          <textarea
            rows={3}
            readOnly={locked}
            value={fieldValue(formData, field)}
            placeholder={field.placeholder}
            onChange={(e) => setFormData((d) => ({ ...d, [field.id]: e.target.value }))}
          />
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <div key={field.id} className={wideClass}>
          <label>{field.label}{field.required ? ' *' : ''}</label>
          <select
            disabled={locked}
            value={fieldValue(formData, field)}
            onChange={(e) => setFormData((d) => ({ ...d, [field.id]: e.target.value }))}
          >
            <option value="">Выберите…</option>
            {(field.options || []).map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div key={field.id} className={wideClass}>
        <label>{field.label}{field.required ? ' *' : ''}</label>
        <input
          type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : field.type === 'datetime-local' ? 'datetime-local' : 'text'}
          readOnly={locked}
          min={field.min}
          max={field.max}
          value={fieldValue(formData, field)}
          placeholder={field.placeholder}
          onChange={(e) => setFormData((d) => ({ ...d, [field.id]: e.target.value }))}
        />
      </div>
    );
  };

  const renderWizardStepBody = () => {
    const stepDef = steps[wizardStep];
    if (!stepDef) return null;

    if (isPreviewStep) {
      return (
        <>
          <div className="rg-wizard-steps" style={{ marginBottom: 14 }}>
            {[
              { key: 'PUBLIC', label: 'Публичная страница' },
              { key: 'FORM', label: 'Форма автора' },
              { key: 'CHECKS', label: 'Проверки и решения' },
              { key: 'DOCS', label: 'Документы' },
              { key: 'ACCESS', label: 'Доступ и папки' },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`rg-wizard-step${previewTab === tab.key ? ' is-active' : ''}`}
                onClick={() => setPreviewTab(tab.key as PreviewTab)}
              >
                <strong>{tab.label}</strong>
              </button>
            ))}
          </div>

          {previewTab === 'PUBLIC' && (
            <div className="rg-panel space-y-3">
              <h4>{String(formData.titleRu || currentProject?.title || 'Черновик проекта')}</h4>
              <p style={{ margin: 0, color: '#9eb6c4' }}>
                {String(formData.description || formData.aimsScope || currentProject?.description || 'Официальная площадка публикации научных материалов.')}
              </p>
            </div>
          )}
          {previewTab === 'FORM' && (
            <div className="rg-panel">
              <h4 className="rg-section-title">Поля формы подачи</h4>
              <div className="rg-summary">
                <div className="rg-summary-row"><span>ФИО автора и соавторов</span><span className="rg-ok">Обязательно</span></div>
                <div className="rg-summary-row"><span>Название и аннотация</span><span className="rg-ok">Обязательно</span></div>
                <div className="rg-summary-row"><span>Файл полного текста (DOCX)</span><span className="rg-ok">Обязательно</span></div>
              </div>
            </div>
          )}
          {previewTab === 'CHECKS' && (
            <div className="rg-panel">
              <h4 className="rg-section-title">Регламент рецензирования</h4>
              <p style={{ margin: 0, color: '#9eb6c4' }}>
                {String(formData.reviewType || 'Двойное слепое рецензирование')} · {String(formData.decision || 'Решение по порогу')}
              </p>
            </div>
          )}
          {previewTab === 'DOCS' && (
            <div className="rg-panel">
              <h4 className="rg-section-title">Шаблоны и документы</h4>
              <div className="rg-summary">
                <div className="rg-summary-row"><span>Информационное письмо (PDF)</span><button type="button" className="rg-btn">Скачать</button></div>
                <div className="rg-summary-row"><span>Шаблон статьи (DOCX)</span><button type="button" className="rg-btn">Скачать</button></div>
              </div>
            </div>
          )}
          {previewTab === 'ACCESS' && (
            <div className="rg-panel">
              <h4 className="rg-section-title">Хранение и доступы</h4>
              <div className="rg-summary">
                <div className="rg-summary-row">
                  <span>Папка проекта</span>
                  <span>{currentProject?.drive_path || `/storage/academic/editorial/${currentProject?.project_code || 'draft'}/`}</span>
                </div>
              </div>
            </div>
          )}
        </>
      );
    }

    return (
      <section className="rg-panel">
        <div className="rg-step-head">
          <h3>{stepDef.title}</h3>
          <p>{stepDef.desc}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {stepDef.fields.map(renderField)}
        </div>
      </section>
    );
  };

  const statusLabel = (value?: string | null) => {
    const map: Record<string, string> = {
      DRAFT: 'Черновик',
      PENDING: 'На проверке',
      READY_FOR_REVIEW: 'На проверке',
      ON_MODERATION: 'На модерации',
      APPROVED: 'Одобрен',
      ACTIVE: 'Запущен',
      RETURNED: 'Возвращён',
      CHANGES_REQUESTED: 'Нужны правки',
      REJECTED: 'Отклонён',
      PUBLISHED: 'Опубликован',
    };
    return map[value || ''] || value || '—';
  };

  if (!isOpen) return null;

  return (
    <div className="rg-overlay rg-wizard" role="dialog" aria-modal="true" aria-label="Редакционный проект">
      <div className="rg-shell rg-wizard-shell">
        <div className="rg-top">
          <div className="rg-top-left">
            <button
              type="button"
              onClick={handleBack}
              className="rg-btn"
              data-rg-back-handler={
                view === 'CATEGORY' ? 'U153_EDITORIAL_TO_HUB' : 'R514_STACK'
              }
              data-u153-editorial-from-hub={
                view === 'CATEGORY' && isEditorialOpenedFromHub() ? '1' : undefined
              }
              aria-description={
                view === 'CATEGORY'
                  ? `Return to Create Request Hub (${U153_EDITORIAL_FROM_HUB})`
                  : undefined
              }
            >
              {view === 'CATEGORY' ? '← Вернуться к выбору маршрута' : '← Назад'}
            </button>
            <div>
              <small>R5.14 · EDITORIAL</small>
              <h2>{view === 'WIZARD' ? (kind === 'JOURNAL' ? 'Конструктор журнала' : 'Конструктор конференции') : 'Создать журнал или конференцию'}</h2>
              <p>
                {view === 'WIZARD'
                  ? `Шаг ${wizardStep + 1} из ${steps.length}`
                  : 'Конференция / научное мероприятие или журнал'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setView(view === 'LIST' ? 'CATEGORY' : 'LIST')} className="rg-btn">
              {view === 'LIST' ? '← К категориям' : `Все проекты (${projects.length})`}
            </button>
            <button type="button" onClick={onClose} className="rg-btn">Закрыть ×</button>
          </div>
        </div>

        <div className="rg-wizard-body">
          {message ? (
            <div className="rg-panel" style={{ marginBottom: 14, borderColor: 'rgba(73,221,183,0.45)', color: '#49ddb7' }}>
              {message}
            </div>
          ) : null}

          {view === 'CATEGORY' && (
            <>
              <section className="rg-author-hero rg-editorial-hero">
                <div>
                  <small>R5.14 · CONFERENCE / JOURNAL</small>
                  <h3>Что вы хотите создать?</h3>
                  <p>
                    Конференция, семинар, симпозиум или постоянный научный журнал. После выбора
                    открывается полный конструктор: календарь, правила, рецензирование и модерация.
                  </p>
                </div>
              </section>
              <div className="rg-route-home-grid rg-route-home-grid--editorial">
                <button
                  type="button"
                  disabled={busy}
                  className="rg-route-home-card rg-route-home-card--xl rg-route-home-card--editorial"
                  style={{ '--rg-accent': '#49ddb7' } as React.CSSProperties}
                  onClick={() => startCategory('CONFERENCE')}
                  data-r514-kind="CONFERENCE"
                >
                  <span className="rg-route-home-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <path d="M4 5h16v15H4zM7 3v4M17 3v4M4 9h16M8 13h3M13 13h3M8 16h8" />
                    </svg>
                  </span>
                  <small className="rg-route-home-num">01</small>
                  <h4>Конференция / научное мероприятие</h4>
                  <p>Конференция, семинар, симпозиум, форум, круглый стол и внутренний приём материалов</p>
                  <span>Открыть мастер →</span>
                </button>
                <button
                  type="button"
                  disabled={busy}
                  className="rg-route-home-card rg-route-home-card--xl rg-route-home-card--editorial"
                  style={{ '--rg-accent': '#e7b13a' } as React.CSSProperties}
                  onClick={() => startCategory('JOURNAL')}
                  data-r514-kind="JOURNAL"
                >
                  <span className="rg-route-home-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <path d="M5 3h11l3 3v15H5V3zM16 3v4h4M8 11h8M8 15h8M8 7h4" />
                    </svg>
                  </span>
                  <small className="rg-route-home-num">02</small>
                  <h4>Журнал</h4>
                  <p>Периодическое научное издание, индексация, рецензирование и роли редколлегии</p>
                  <span>Открыть мастер →</span>
                </button>
              </div>
            </>
          )}

          {view === 'WIZARD' && currentProject && (
            <div className="rg-editorial-layout">
              <nav className="rg-wizard-steps rg-profile-steps">
                {steps.map((step, index) => (
                  <button
                    key={step.id}
                    type="button"
                    className={`rg-wizard-step${wizardStep === index ? ' is-active' : ''}`}
                    onClick={() => goStep(index)}
                  >
                    <small>{index + 1}</small>
                    <strong>{step.title}</strong>
                  </button>
                ))}
              </nav>
              <div className="rg-profile-main">
              {renderWizardStepBody()}
              <div className="rg-etalon-actions">
                <button type="button" className="rg-btn" onClick={handleBack}>← Назад</button>
                <button type="button" disabled={busy} className="rg-btn" onClick={() => saveDraft()}>
                  Сохранить черновик
                </button>
                <span style={{ flex: 1 }} />
                {!isPreviewStep ? (
                  <button type="button" className="rg-btn primary" onClick={() => goStep(wizardStep + 1)}>
                    Далее →
                  </button>
                ) : (
                  <button type="button" disabled={busy} className="rg-btn primary" onClick={submitProject}>
                    {busy ? 'Отправка…' : 'Отправить на модерацию →'}
                  </button>
                )}
              </div>
              </div>
            </div>
          )}

          {view === 'PREVIEW' && currentProject && (
            <>
              <div className="rg-panel" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                <div>
                  <div className="rg-chip-row" style={{ marginBottom: 6 }}>
                    <span className="rg-chip is-amber">{currentProject.project_code}</span>
                    <span className="rg-chip">{statusLabel(currentProject.status)}</span>
                  </div>
                  <h3 style={{ margin: 0 }}>{currentProject.title}</h3>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="rg-btn primary" onClick={() => handleModerate(currentProject.id, 'APPROVE')}>
                    Одобрить
                  </button>
                  <button type="button" className="rg-btn" onClick={() => handleModerate(currentProject.id, 'CHANGES_REQUESTED')}>
                    Запросить правки
                  </button>
                </div>
              </div>
              {renderWizardStepBody()}
            </>
          )}

          {view === 'LIST' && (
            <div className="space-y-3">
              <div className="rg-step-head">
                <h3>Реестр редакционных проектов</h3>
                <p>Конференции и журналы со статусом модерации.</p>
              </div>
              {projects.length === 0 ? (
                <div className="rg-panel" style={{ textAlign: 'center', color: '#86a6b6' }}>
                  Проекты ещё не созданы.
                </div>
              ) : (
                projects.map((p) => (
                  <div key={p.id} className="rg-panel" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                    <div>
                      <div className="rg-chip-row" style={{ marginBottom: 8 }}>
                        <span className="rg-chip is-amber">{p.project_code}</span>
                        <span className="rg-chip">{p.kind === 'CONFERENCE' ? 'Конференция' : 'Журнал'}</span>
                        <span className="rg-chip">{statusLabel(p.status)}</span>
                      </div>
                      <h4 style={{ margin: '0 0 4px' }}>{p.title || 'Без названия'}</h4>
                      <p style={{ margin: 0, color: '#86a6b6', fontSize: 12 }}>
                        {p.organizer || '—'} · шаг {(p.step ?? 0) + 1}
                      </p>
                    </div>
                    <button type="button" onClick={() => continueDraft(p)} className="rg-btn primary">
                      Продолжить →
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
